<template>
  <v-container
    fluid
    fill-height
  >
    <div class="text-center">
      <v-overlay :value="isFormLoading">
        <v-progress-circular
          indeterminate
          size="64"
        />
      </v-overlay>
    </div>

    <v-row>
      <v-col class="pt-sm-3">
        <div
          class="share-register-ctn"
          role="share-register-ctn"
        >
          <v-row>
            <v-col class="text-right">
              <add-new-button
                title="Add shares"
                @click="showAddShareDialog = true"
              />
            </v-col>
          </v-row>
          <v-card
            :loading="sharesLoading"
            flat
          >
            <v-card-text
              v-if="sharesLoading"
              role="loading-text"
            >
              {{ loadingOrNoDataText }}
            </v-card-text>
            <v-expansion-panels
              flat
              multiple
              accordion
              :value="getSeriesSectionIndexes()"
              role="expansion-panels"
            >
              <v-expansion-panel
                v-for="(seriesItem, i) in series"
                :key="i"
                role="expansion-panel"
              >
                <v-expansion-panel-header>
                  <h2>{{ seriesHeader(seriesItem) }}</h2>
                  <div class="open-chip">
                    <v-chip
                      v-if="false /* disabled until implemented */"
                      color="accent"
                      label
                      outlined
                    >
                      {{ seriesStatus(seriesItem) }}
                    </v-chip>
                  </div>
                  <template #actions>
                    <v-icon color="gray">
                      $expand
                    </v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    ref="datatable"
                    role="data-table"
                    :headers="headers"
                    :items="filterShareItemsForSeries(seriesItem)"
                    :no-data-text="loadingOrNoDataText"
                    :group-desc="false"
                    :class="{
                      'hide-colgroup table-fixed': $vuetify.breakpoint.xs,
                      'table-is-mobile': tableIsMobile
                    }"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-btn
              v-if="false /* disabled until implemented */"
              color="primarydd"
              outlined
            >
              Close Series
            </v-btn>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <v-dialog
      v-model="showAddShareDialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title>Add Share</v-card-title>
        <v-card-text>
          <form-share
            v-if="showAddShareDialog"
            :loading="isFormLoading"
            text-action-btn="Add Share"
            @onSubmitData="onShareActionData"
            @onCancelClick="onCancelAction"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import AddNewButton from '../components/AddNewButton'
import { getRootCollection } from '@/dataAccess/queries'
import FormShare from '@/components/createFromTemplate/FormShare.vue'

const openSeries = ['B']
const isOpenSeries = (series) => openSeries.includes(series)
const FOUNDERS_SERIES_NAME = 'Founders'

const seriesOrder = [FOUNDERS_SERIES_NAME, 'A', 'B', 'C']

export default {
  name: 'ShareRegister',
  components: { AddNewButton, FormShare },
  data: function () {
    return {
      sharesLoading: true,
      tableIsMobile: false,
      showAddShareDialog: false,
      expanded: false,
      isFormLoading: false
    }
  },
  computed: {
    headers() {
      const headers = [
        { text: 'Shareholder', value: 'shareholder', hidden: false },
        { text: 'Email', value: 'email', hidden: false },
        { text: 'Certificate Number', value: 'certificateNumber', hidden: false },
        { text: 'No. Issued Shares', value: 'noIssued', hidden: false },
        { text: 'Type', value: 'shareType', hidden: false },
        { text: 'Date of issue', value: 'dateIssued', hidden: false },
        { text: 'Percentage', value: 'percentage', hidden: false }
      ]

      return headers.filter((header) => !header.hidden)
    },
    shareItems() {
      if (this.sharesLoading) {
        return []
      }

      return this.accountShares.filter(
        (item) =>
          typeof item.documentId === 'undefined' ||
          (item.documentId && item.docSigned)
      )
    },
    loadingOrNoDataText() {
      return this.sharesLoading
        ? 'Loading shares...'
        : this.shareItems.length > 0
          ? 'Data hidden.'
          : 'No data.'
    },
    availableSeries() {
      return [...new Set(this.shareItems.map((item) => item.series))]
    },
    series() {
      return seriesOrder.filter((serie) => this.availableSeries.includes(serie))
    }
  },
  beforeCreate() {
    this.dispatchActions(['get_appUser', 'get_account'])
    this.mapStates(['appUser', 'account', 'shares'])
    this.mapGetters(['accountShares'])
    this.$store.dispatch('get_shares').then(() => {
      this.sharesLoading = false
    })
  },
  methods: {
    seriesStatus(series) {
      return isOpenSeries(series) ? 'Open' : 'Closed'
    },
    getSeriesSectionIndexes() {
      return Array.from({ length: this.series.length }, (x, i) => i)
    },
    filterShareItemsForSeries(series) {
      return this.shareItems.filter((item) => item.series === series)
    },
    seriesHeader(series) {
      // The founders series doesn't need to be prefixed
      if (series === FOUNDERS_SERIES_NAME) {
        return series
      }
      return `Series ${series}`
    },
    async onShareActionData(formData) {
      this.showAddShareDialog = false
      this.isFormLoading = true

      if (formData) {
        await getRootCollection('shareRegister').add({
          ...formData,
          companyName: this.account.name,
          accountId: this.account.id,
          userId: this.appUser.id
        }).then(() => {
          this.$showSuccessbar('Share added!')
        }).catch(() => {
          this.$showError('Something went wrong!')
        })
      }

      this.onCancelAction()
    },
    onCancelAction() {
      this.isFormLoading = false
      this.showAddShareDialog = false
    }
  }
}
</script>

<style lang="scss">
.share-register-ctn {
  padding: 16px;

  .v-card {
    margin: 16px 0;

    .v-btn {
      margin-top: 25px;
    }
  }

  .v-expansion-panel {
    border-bottom: 1px solid var(--v-slategray-base);
    border-radius: 0;
  }

  .v-item--active {
    .v-expansion-panel-header {
      .v-chip {
        display: inline-flex;
      }
    }
  }

  .v-expansion-panel-header {
    padding: 16px 0;

    h2 {
      flex: none;
    }

    .v-chip {
      margin-left: 25px;
      display: none;
    }
  }

  .v-expansion-panel-content {
    > div {
      padding: 0;
    }
  }

  .v-data-table {
    .text-start {
      padding: 0 16px 0 0;
    }

    .v-data-table-header {
      .text-start {
        color: var(--v-slategray-base);
        text-transform: uppercase;
      }
    }
  }
}
</style>
